import { useLanguage } from '@/hooks/useLanguage';
import { ContactForm } from '@/components/ContactForm';
import { Button } from '@/components/ui/button';
import { MapPin, Mail, Phone, Users, Download, Building, ShoppingBag, MapPinCheckIcon } from 'lucide-react';

export const Contact = () => {
  const { t } = useLanguage();

  const contactInfo = [
    {
      icon: MapPinCheckIcon,
      label: t.contact.ip,
      value: t.contact.info.ip
    },
    {
      icon: Building,
      label: t.contact.menufacturer,
      value: t.contact.info.menufacturer
    },
    {
      icon: ShoppingBag,
      label: t.contact.distributor,
      value: t.contact.info.distributor
    },
    {
      icon: Mail,
      label: t.contact.form.email,
      value: t.contact.info.email
    },
    {
      icon: Phone,
      label: t.contact.form.phone,
      value: t.contact.info.phone
    }
  ];

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            {t.contact.title}
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {t.contact.subtitle}
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-primary to-secondary mx-auto mt-6 rounded-full" />
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Information */}
          <div className="space-y-8">
            {/* Contact Details */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-gray-900">
                {t.contact.title}
              </h3>

              <div className="space-y-4">
                {contactInfo.map((info, index) => {
                  const IconComponent = info.icon;
                  return (
                    <div key={index} className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <IconComponent className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{info.label}</p>
                        <p className="text-gray-600">{info.value}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-gray-900">
                {t.contact.quickAction}
              </h4>

              <div className="space-y-3">
                <Button
                  variant="outline"
                  className="w-full justify-start border-primary text-primary hover:bg-primary hover:text-white"
                  onClick={() => console.log('Download catalog')}
                >
                  <Download className="w-4 h-4 mr-3" />
                  {t.hero.ctaCatalog}
                </Button>

                <Button
                  variant="outline"
                  className="w-full justify-start border-secondary text-secondary hover:bg-secondary hover:text-white"
                  onClick={() => console.log('Become distributor')}
                >
                  <Users className="w-4 h-4 mr-3" />
                  {t.hero.ctaDistributor}
                </Button>
              </div>
            </div>

            {/* Map Placeholder */}
            <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl h-64 flex items-center justify-center">
              <div className="text-center space-y-2">
                <MapPin className="w-12 h-12 text-gray-400 mx-auto" />
                <p className="text-gray-500 font-medium">
                  {t.language === 'en' ? 'Bangkok, Thailand' : 'กรุงเทพฯ ประเทศไทย'}
                </p>
                <p className="text-sm text-gray-400">
                  {t.language === 'en' ? 'N/A' : 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-8">
            <div className="mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {t.contact.title}
              </h3>
              <p className="text-gray-600">
                {t.contact.form.description}
              </p>
            </div>

            <ContactForm />
          </div>
        </div>

        {/* Business Hours */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-primary/5 to-secondary/5 rounded-2xl p-8 max-w-2xl mx-auto">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {t.contact.businessHours.title}
            </h3>
            <div className="grid sm:grid-cols-2 gap-4 text-gray-600">
              <div>
                <p className="font-semibold">
                  {t.contact.businessHours.monToFri}
                </p>
                <p>{t.contact.businessHours.monToFriHour}</p>
              </div>
              <div>
                <p className="font-semibold">
                  {t.contact.businessHours.sat}
                </p>
                <p>{t.contact.businessHours.satHour}</p>
              </div>
            </div>
            <p className="text-sm text-gray-500 mt-4">
              {t.contact.businessHours.description}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};